import React from "react";
import Chart from "react-apexcharts";
import { Box, Card, CardContent, Typography } from "@mui/material";

interface SparkCardProps {
  title: string;
  value: number | string;
  data: number[];
  categories?: string[];
  bgColor?: string;
  group: string;
}

export default function SparkCard({
  title,
  value,
  data,
  categories,
  bgColor = "#2563eb",
  group,
}: SparkCardProps) {
  const accentColor = bgColor;
  const options: any = {
    chart: {
      id: "spark",
      group: group,
      type: "line",
      height: 70,
      sparkline: { enabled: true },
      dropShadow: {
        enabled: false,
      },
      toolbar: { show: false },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    markers: {
      size: 2,
    },
    grid: {
      padding: {
        top: 10,
        bottom: 10,
        left: 0,
        right: 0,
      },
    },
    colors: [accentColor],
    tooltip: {
      enabled: true,
      x: {
        formatter: function (val: any, opts: any) {
          if (categories && categories.length > opts.dataPointIndex) {
            return categories[opts.dataPointIndex];
          }
          return val;
        },
      },
      y: {
        formatter: (val: number) => `${val}`,
      },
      theme: "light" as const,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.16,
        opacityFrom: 0.42,
        opacityTo: 0.04,
        stops: [0, 90, 100],
      },
    },
  };

  if (categories) {
    options.xaxis = { categories: categories };
  }

  const series = [
    {
      name: title,
      data: data,
    },
  ];

  return (
    <Card
      sx={{
        height: "100%",
        overflow: "hidden",
        position: "relative",
        "&:before": {
          bgcolor: accentColor,
          content: '""',
          height: 3,
          left: 0,
          position: "absolute",
          right: 0,
          top: 0,
        },
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 1.5 } }}>
        <Box sx={{ alignItems: "baseline", display: "flex", gap: 1, mb: 0.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {value}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontWeight: 700, mb: 1 }}
        >
          {title}
        </Typography>
        <Chart options={options} series={series} type="area" height={76} />
      </CardContent>
    </Card>
  );
}
